import { CHART_COLORS, VEGA_SPEC_LEGEND_SETTINGS } from 'constants/chart-spec';
import { CHART_CATEGORIES } from 'constants/chart-categories';
import { get, merge } from 'lodash';
import { generateVegaLabelTextExpression } from 'utils/chart/vega-expressions';
import { generateVegaTooltip } from 'utils/chart/tooltips';
import {
  generateVegaSortParams,
  generateVegaTypeCoercionTransforms,
  generateVegaSortingAndLimitingTransforms,
} from 'utils/chart/transforms';

export default {
  name: 'Donut',
  displayName: 'Donut',
  order: 0,
  flavour: 'discrete',
  category: CHART_CATEGORIES.CIRCULAR,
  iconValue: 'donut',
  channels: [
    {
      name: 'label',
      displayName: 'Label',
      required: true,
      channelType: 'category',
      intent: 'category-axis',
    },
    {
      name: 'value',
      displayName: 'Arc',
      required: true,
      channelType: 'aggregation',
      intent: 'value-axis',
    },
  ],
  specFn: (encodings, customisations) => {
    return {
      data: [
        {
          name: 'transformedData',
          source: 'values',
          transform: [
            ...generateVegaTypeCoercionTransforms('value'),
            ...generateVegaSortingAndLimitingTransforms(encodings),
            {
              type: 'pie',
              field: 'value',
              startAngle: 0,
              endAngle: 6.29,
              sort: false,
            },
          ],
        },
      ],
      autosize: {
        type: 'fit',
        contains: 'padding',
      },
      signals: [
        {
          name: 'shortEdge',
          description: 'the smaller of width and height',
          update: 'width < height ? width : height',
        },
        {
          name: 'outerRadius',
          description: 'Outer radius of a donut chart',
          update: 'shortEdge / 2',
        },
        {
          name: 'innerRadius',
          description: 'Outer radius of a donut chart',
          update: 'outerRadius / 1.6180338', // golden ratio
        },
      ],
      scales: [
        {
          name: 'color',
          type: 'ordinal',
          range:
            get(customisations.options, 'colorDiscrete.value') || CHART_COLORS,
          domain: {
            data: 'transformedData',
            field: 'label',
            sort: generateVegaSortParams(encodings).label,
          },
        },
      ],
      legends: [
        merge({}, VEGA_SPEC_LEGEND_SETTINGS, {
          fill: 'color',
          padding: 0,
          title: get(encodings, 'label.legend.title', ''),
          encode: {
            labels: {
              update: {
                limit: { value: 150 },
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.label,
                    customisations.channels.label
                  ),
                },
              },
            },
          },
        }),
      ],
      marks: [
        {
          type: 'arc',
          from: { data: 'transformedData' },
          encode: {
            enter: {
              y: { signal: 'height / 2' },
              x: { signal: 'width / 2' },
              fill: { scale: 'color', field: 'label' },
              tooltip: {
                signal: generateVegaTooltip(encodings, customisations),
              },
            },
            update: {
              startAngle: { field: 'startAngle' },
              endAngle: { field: 'endAngle' },
              padAngle: { value: 0 },
              innerRadius: { signal: 'innerRadius' },
              outerRadius: { signal: 'outerRadius' },
            },
          },
        },
        ...(!get(customisations.options, 'dataValueLabels.enabled')
          ? []
          : [
              {
                type: 'text',
                from: { data: 'transformedData' },
                encode: {
                  enter: {
                    text: {
                      signal: generateVegaLabelTextExpression(
                        encodings.value,
                        customisations.channels.value
                      ),
                    },
                    x: { signal: 'width / 2' },
                    y: { signal: 'height / 2' },
                    theta: {
                      signal: '(datum.startAngle + datum.endAngle) / 2',
                    },
                    radius: { signal: 'outerRadius', offset: '8' },
                    baseline: { value: 'middle' },
                    align: [
                      {
                        test: '(datum.startAngle + datum.endAngle) / 2 > PI',
                        value: 'right',
                      },
                      { value: 'left' },
                    ],
                  },
                },
              },
            ]),
        ...(!get(customisations.options, 'dataCategoryLabels.enabled')
          ? []
          : [
              {
                type: 'text',
                from: { data: 'transformedData' },
                encode: {
                  enter: {
                    text: { field: 'label' },
                    x: { signal: 'width / 2' },
                    y: { signal: 'height / 2' },
                    theta: {
                      signal: '(datum.startAngle + datum.endAngle) / 2',
                    },
                    radius: { signal: 'outerRadius', offset: '8' },
                    baseline: { value: 'middle' },
                    align: [
                      {
                        test: '(datum.startAngle + datum.endAngle) / 2 > PI',
                        value: 'right',
                      },
                      { value: 'left' },
                    ],
                    dy: [
                      {
                        test:
                          '(datum.startAngle + datum.endAngle) / 2 < PI * 0.5',
                        value: -15,
                      },
                      {
                        test:
                          '(datum.startAngle + datum.endAngle) / 2 > PI * 1.5',
                        value: -15,
                      },
                      { value: 15 },
                    ],
                  },
                },
              },
            ]),
      ],
    };
  },
};
